import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import DraftInterface from '../components/DraftInterface';
import PullToRefresh from '../components/PullToRefresh';
import { authAxios } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ListOrdered, CheckCircle2, Clock } from 'lucide-react';

// Component to show auto-draft ranking prompt
function AutoDraftRankingCard({ seasonId, API, userId }) {
  const navigate = useNavigate();
  const [draftInfo, setDraftInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDraftStatus = async () => {
      try {
        const response = await authAxios.get(`${API}/seasons/${seasonId}/draft/rankings/status`);
        setDraftInfo(response.data);
      } catch (error) {
        // If error, draft might not be in auto mode - just don't show the card
        console.log('Draft not in auto mode or not configured');
      } finally {
        setLoading(false);
      }
    };

    if (seasonId) {
      loadDraftStatus();
    }
  }, [seasonId, API]);

  // Don't show if not auto mode, loading, or user is not a participant
  if (loading || !draftInfo || draftInfo.draft_mode !== 'auto' || !draftInfo.is_participant) {
    return null;
  }

  // If draft is completed and within 1 week, show replay link
  if (draftInfo.draft_started && draftInfo.show_replay_card) {
    return (
      <Card className="spacing-card bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-slate-800">
                Draft Complete!
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                The auto-draft has finished. View the replay to see how contestants were assigned.
              </p>
              
              <Button 
                onClick={() => navigate(`/draft/${seasonId}/replay`)}
                className="mt-4 bg-green-600 hover:bg-green-700"
              >
                View Draft Replay
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Don't show card if draft started but replay card expired (> 1 week)
  if (draftInfo.draft_started) {
    return null;
  }

  // Find current user's status
  const myStatus = draftInfo.players?.find(p => p.user_id === userId);
  const hasSubmitted = myStatus?.submitted_at;

  return (
    <Card className="spacing-card bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
      <CardContent className="p-4 md:p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
            {hasSubmitted ? (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            ) : (
              <ListOrdered className="w-6 h-6 text-purple-600" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-slate-800">
              {hasSubmitted ? 'Rankings Submitted!' : 'Rank Your Contestants'}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {hasSubmitted 
                ? "Your preferences are locked in. The admin will run the auto-draft soon."
                : "This league uses Auto-Draft. Rank contestants in order of preference - your top choices will be picked first!"}
            </p>
            
            {/* Status indicator */}
            <div className="flex items-center gap-2 mt-3 text-sm">
              <Clock className="w-4 h-4 text-purple-500" />
              <span className="text-purple-700">
                {draftInfo.submitted_count} of {draftInfo.total_players} players have submitted rankings
              </span>
            </div>
            
            <Button 
              onClick={() => navigate('/draft/rankings')}
              className={`mt-4 ${hasSubmitted ? 'bg-slate-600 hover:bg-slate-700' : 'bg-purple-600 hover:bg-purple-700'}`}
            >
              {hasSubmitted ? 'View Your Rankings' : 'Rank Contestants Now'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Home() {
  const { user, currentSeason, API } = useContext(AppContext);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRoster, setMyRoster] = useState([]);
  const [activeBonusQuestions, setActiveBonusQuestions] = useState([]);
  const [bonusAnswers, setBonusAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [platformAnnouncement, setPlatformAnnouncement] = useState(null);
  const [announcementCollapsed, setAnnouncementCollapsed] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem('platformAnnouncementCollapsed') === 'true';
  });

  // Toggle announcement collapse state and persist to localStorage
  const toggleAnnouncementCollapsed = () => {
    const newState = !announcementCollapsed;
    setAnnouncementCollapsed(newState);
    localStorage.setItem('platformAnnouncementCollapsed', String(newState));
  };

  useEffect(() => {
    if (currentSeason) {
      loadData();
    }
    // Always load platform announcement (even without season)
    loadPlatformAnnouncement();
  }, [currentSeason]);

  const loadPlatformAnnouncement = async () => {
    try {
      const response = await authAxios.get(`${API}/platform/announcement`);
      if (response.data?.show_announcement && response.data?.announcement_content) {
        setPlatformAnnouncement(response.data);
      } else {
        setPlatformAnnouncement(null);
      }
    } catch (error) {
      // Don't show error - just hide announcement
      console.error('Failed to load platform announcement:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [leaderboardRes, rosterRes, bonusRes] = await Promise.all([
        authAxios.get(`${API}/seasons/${currentSeason.id}/leaderboard`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/my-roster`),
        authAxios.get(`${API}/bonus-questions/active`).catch(() => ({ data: [] }))
      ]);
      setLeaderboard(leaderboardRes.data);
      setMyRoster(rosterRes.data);
      setActiveBonusQuestions(bonusRes.data || []);
      
      // Pre-fill answers if user already answered
      const answers = {};
      if (bonusRes.data) {
        bonusRes.data.forEach(q => {
          if (q.user_answer) {
            answers[q.id] = q.user_answer.answer_value;
          }
        });
      }
      setBonusAnswers(answers);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const myPoints = leaderboard.find(entry => entry.user_id === user?.id)?.total_points || 0;
  const myRank = leaderboard.findIndex(entry => entry.user_id === user?.id) + 1;
  const activeContestants = myRoster.filter(r => r.contestant?.status === 'active').length;
  
  const showNotes = currentSeason?.show_notes || false;
  const notesTitle = currentSeason?.notes_title || 'Gamekeeper\'s Notes';
  const notesContent = currentSeason?.notes_content || '';
  const notesUpdatedAt = currentSeason?.notes_updated_at;
  
  const formatNotesDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const submitBonusAnswer = async (questionId) => {
    const answer = bonusAnswers[questionId];
    if (!answer || !answer.trim()) {
      toast.error('Please provide an answer');
      return;
    }

    try {
      await authAxios.post(
        `${API}/bonus-answers`,
        {
          bonus_question_id: questionId,
          answer_value: answer
        }
      );
      toast.success('Answer submitted!');
      loadData();
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    }
  };

  const getTimeRemaining = (closesAt) => {
    const now = new Date();
    const closes = new Date(closesAt);
    const diff = closes - now;
    
    if (diff <= 0) return 'Closed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  return (
    <Layout>
      <PullToRefresh onRefresh={loadData}>
        <div className="max-w-4xl mx-auto" data-testid="home-dashboard">
          {/* Header */}
          <div className="spacing-section">
            <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-responsive text-slate-600">
              Season {currentSeason?.number} • {currentSeason?.status?.toUpperCase()}
            </p>
          </div>

          {/* Auto-Draft Card - Shows for auto-draft mode (before start OR after completion) */}
          {(currentSeason?.draft_status === 'not_started' || currentSeason?.draft_status === 'completed') && (
            <AutoDraftRankingCard seasonId={currentSeason?.id} API={API} userId={user?.id} />
          )}

          {/* Draft Interface - Shows when draft is in progress */}
          {currentSeason?.draft_status === 'in_progress' && <DraftInterface />}

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
            </div>
          ) : (
            <>
            {/* Bonus Questions - Grouped by Episode */}
            {activeBonusQuestions.length > 0 && (() => {
              // Group questions by episode
              const questionsByEpisode = activeBonusQuestions.reduce((acc, q) => {
                const episodeId = q.episode_id;
                if (!acc[episodeId]) {
                  acc[episodeId] = {
                    episode: q.episode,
                    questions: [],
                    closes_at: q.closes_at
                  };
                }
                acc[episodeId].questions.push(q);
                return acc;
              }, {});

              return Object.values(questionsByEpisode).map((episodeGroup, groupIdx) => (
                <Card key={groupIdx} className="spacing-card bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
                  <CardHeader className="padding-card pb-2 md:pb-4">
                    <div className="card-mobile-horizontal">
                      <div className="mb-2 sm:mb-0">
                        <CardTitle className="heading-card text-indigo-900 flex items-center gap-2">
                          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-base md:text-lg">
                            Bonus Question{episodeGroup.questions.length > 1 ? 's' : ''}
                          </span>
                        </CardTitle>
                        <p className="text-xs md:text-sm text-indigo-700 mt-1">
                          Episode {episodeGroup.episode?.number || 'TBD'}{episodeGroup.episode?.title ? `: ${episodeGroup.episode.title}` : ''}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <span className="text-xs md:text-sm text-indigo-600 font-medium">
                          ⏰ {getTimeRemaining(episodeGroup.closes_at)}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="padding-card pt-0 space-y-4 md:space-y-6">
                    {episodeGroup.questions.map((question, qIdx) => (
                      <div key={question.id} className={qIdx > 0 ? "pt-6 border-t border-indigo-200" : ""}>
                        {episodeGroup.questions.length > 1 && (
                          <div className="text-xs font-semibold text-indigo-600 mb-2">
                            Question {qIdx + 1} of {episodeGroup.questions.length}
                          </div>
                        )}
                        
                        <p className="text-base md:text-lg font-medium text-slate-800 mb-3 md:mb-4">{question.question_text}</p>
                        
                        {question.answer_type === 'yes_no' && (
                          <div className="flex gap-3 md:gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`bonus_answer_${question.id}`}
                                value="Yes"
                                checked={bonusAnswers[question.id] === 'Yes'}
                                onChange={(e) => setBonusAnswers({...bonusAnswers, [question.id]: e.target.value})}
                                disabled={question.user_answer && !question.allow_answer_edits}
                                className="w-4 h-4 text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              <span className="text-sm md:text-base">Yes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`bonus_answer_${question.id}`}
                                value="No"
                                checked={bonusAnswers[question.id] === 'No'}
                                onChange={(e) => setBonusAnswers({...bonusAnswers, [question.id]: e.target.value})}
                                disabled={question.user_answer && !question.allow_answer_edits}
                                className="w-4 h-4 text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              <span className="text-sm md:text-base">No</span>
                            </label>
                          </div>
                        )}
                        
                        {question.answer_type === 'multiple_choice' && (
                          <div className="space-y-2">
                            {question.options?.map((option, idx) => (
                              <label key={idx} className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-indigo-50">
                                <input
                                  type="radio"
                                  name={`bonus_answer_${question.id}`}
                                  value={option}
                                  checked={bonusAnswers[question.id] === option}
                                  onChange={(e) => setBonusAnswers({...bonusAnswers, [question.id]: e.target.value})}
                                  disabled={question.user_answer && !question.allow_answer_edits}
                                  className="w-4 h-4 text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed mt-0.5 flex-shrink-0"
                                />
                                <span className="text-sm md:text-base">{option}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        
                        {question.answer_type === 'free_text' && (
                          <Textarea
                            value={bonusAnswers[question.id] || ''}
                            onChange={(e) => setBonusAnswers({...bonusAnswers, [question.id]: e.target.value})}
                            placeholder="Type your answer..."
                            disabled={question.user_answer && !question.allow_answer_edits}
                            rows={3}
                            className="text-sm md:text-base"
                          />
                        )}
                        
                        <div className="card-mobile-horizontal pt-3 md:pt-4 gap-2">
                          <span className="text-xs md:text-sm text-slate-600 font-medium">
                            Worth {question.points_value} points
                          </span>
                          {(!question.user_answer || question.allow_answer_edits) && (
                            <Button onClick={() => submitBonusAnswer(question.id)} className="btn-mobile-full text-sm">
                              {question.user_answer ? 'Update Answer' : 'Submit Answer'}
                            </Button>
                          )}
                        </div>
                        
                        {question.user_answer && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                            <p className="text-sm text-blue-700">
                              ✓ You submitted: <strong>{question.user_answer.answer_value}</strong>
                              {!question.allow_answer_edits && (
                                <span className="ml-2 text-xs">(Answer locked)</span>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ));
            })()}

            {/* Platform Announcement (Super Admin) */}
            {platformAnnouncement && (
              <Card className={`spacing-card border ${
                platformAnnouncement.announcement_type === 'warning' 
                  ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300'
                  : platformAnnouncement.announcement_type === 'success'
                  ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300'
                  : 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-300'
              }`}>
                <CardHeader className="padding-card pb-2 md:pb-4">
                  <div className="flex items-center justify-between">
                    <div className="card-mobile-horizontal flex-1">
                      <CardTitle className={`heading-card flex items-center gap-2 ${
                        platformAnnouncement.announcement_type === 'warning' 
                          ? 'text-amber-900'
                          : platformAnnouncement.announcement_type === 'success'
                          ? 'text-emerald-900'
                          : 'text-blue-900'
                      }`}>
                        {platformAnnouncement.announcement_type === 'warning' ? (
                          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        ) : platformAnnouncement.announcement_type === 'success' ? (
                          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                        <span className="text-base md:text-lg">{platformAnnouncement.announcement_title || 'Announcement'}</span>
                      </CardTitle>
                      {platformAnnouncement.announcement_updated_at && !announcementCollapsed && (
                        <span className={`text-xs md:text-sm flex items-center gap-1 ${
                          platformAnnouncement.announcement_type === 'warning' 
                            ? 'text-amber-700'
                            : platformAnnouncement.announcement_type === 'success'
                            ? 'text-emerald-700'
                            : 'text-blue-700'
                        }`}>
                          <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatNotesDate(platformAnnouncement.announcement_updated_at)}
                        </span>
                      )}
                    </div>
                    {/* Collapse/Expand Button */}
                    <button
                      onClick={toggleAnnouncementCollapsed}
                      className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${
                        platformAnnouncement.announcement_type === 'warning'
                          ? 'hover:bg-amber-200/50 text-amber-700'
                          : platformAnnouncement.announcement_type === 'success'
                          ? 'hover:bg-emerald-200/50 text-emerald-700'
                          : 'hover:bg-blue-200/50 text-blue-700'
                      }`}
                      title={announcementCollapsed ? 'Expand announcement' : 'Collapse announcement'}
                      aria-label={announcementCollapsed ? 'Expand announcement' : 'Collapse announcement'}
                    >
                      <svg 
                        className={`w-5 h-5 transition-transform duration-200 ${announcementCollapsed ? '' : 'rotate-180'}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </CardHeader>
                {!announcementCollapsed && (
                  <CardContent className="padding-card pt-0">
                    <div className="prose prose-sm md:prose max-w-none text-slate-700 whitespace-pre-wrap">{platformAnnouncement.announcement_content}</div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Gamekeeper's Notes */}
            {showNotes && notesContent && (
              <Card className="spacing-card bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                <CardHeader className="padding-card pb-2 md:pb-4">
                  <div className="card-mobile-horizontal">
                    <CardTitle className="heading-card text-purple-900 flex items-center gap-2">
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span className="text-base md:text-lg">{notesTitle}</span>
                    </CardTitle>
                    {notesUpdatedAt && (
                      <span className="text-xs md:text-sm text-purple-600 flex items-center gap-1">
                        <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatNotesDate(notesUpdatedAt)}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="padding-card pt-0">
                  <div className="prose prose-sm md:prose max-w-none text-slate-700 whitespace-pre-wrap">{notesContent}</div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards - Responsive: Mobile stacked/grouped, Desktop 3 columns */}
            <div className="mb-8">
              {/* Mobile: My Total Points full width, then Roster + Standing side by side */}
              {/* Desktop: All 3 in a row */}
              <div className="flex flex-col md:hidden gap-4">
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                  <CardHeader className="padding-card pb-2">
                    <CardTitle className="text-slate-700 text-base">My Total Points</CardTitle>
                  </CardHeader>
                  <CardContent className="padding-card pt-0">
                    <div className="text-4xl font-bold text-amber-600">{myPoints}</div>
                    <p className="text-xs text-slate-600 mt-2">Season {currentSeason?.number}</p>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-2 gap-3">
                  <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-slate-700 text-sm">My Roster</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0">
                      <div className="text-3xl font-bold text-emerald-600">
                        {activeContestants}/{myRoster.length}
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1">Active / Total</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-slate-700 text-sm">Current Standing</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0">
                      <div className={`text-3xl font-bold ${
                        myRank === 1 ? 'text-amber-500' :
                        myRank === 2 ? 'text-slate-400' :
                        myRank === 3 ? 'text-orange-400' :
                        'text-blue-600'
                      }`}>
                        #{myRank || '-'}
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1">
                        {myRank === 1 ? '🥇 First Place!' :
                         myRank === 2 ? '🥈 Second Place' :
                         myRank === 3 ? '🥉 Third Place' :
                         myRank ? `Out of ${leaderboard.length}` : 'Not ranked yet'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Desktop: All 3 cards in a row */}
              <div className="hidden md:grid md:grid-cols-3 gap-6">
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                  <CardHeader className="padding-card pb-2">
                    <CardTitle className="text-slate-700 text-lg">My Total Points</CardTitle>
                  </CardHeader>
                  <CardContent className="padding-card pt-0">
                    <div className="text-5xl font-bold text-amber-600">{myPoints}</div>
                    <p className="text-sm text-slate-600 mt-2">Season {currentSeason?.number}</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                  <CardHeader className="padding-card pb-2">
                    <CardTitle className="text-slate-700 text-lg">My Roster</CardTitle>
                  </CardHeader>
                  <CardContent className="padding-card pt-0">
                    <div className="text-5xl font-bold text-emerald-600">
                      {activeContestants}/{myRoster.length}
                    </div>
                    <p className="text-sm text-slate-600 mt-2">Active / Total</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <CardHeader className="padding-card pb-2">
                    <CardTitle className="text-slate-700 text-lg">Current Standing</CardTitle>
                  </CardHeader>
                  <CardContent className="padding-card pt-0">
                    <div className={`text-5xl font-bold ${
                      myRank === 1 ? 'text-amber-500' :
                      myRank === 2 ? 'text-slate-400' :
                      myRank === 3 ? 'text-orange-400' :
                      'text-blue-600'
                    }`}>
                      #{myRank || '-'}
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      {myRank === 1 ? '🥇 First Place!' :
                       myRank === 2 ? '🥈 Second Place' :
                       myRank === 3 ? '🥉 Third Place' :
                       myRank ? `Out of ${leaderboard.length}` : 'Not ranked yet'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
        </div>
      </PullToRefresh>
    </Layout>
  );
}
